import { inject, Injectable } from '@angular/core';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { TelemetryLoggerService } from '../../core/services/telemetry/telemetry-logger.service';
import {
  buildLocalStorageKeyMaterial,
  deriveLocalStorageEncryptionKey,
} from '../utility/local-storage-crypto.util';
import { clearEncryptedStorageReadCache } from '../utility/local-storage-encryption.util';
import { migrateAllPlainLocalStorageKeys } from '../utility/local-storage-native.util';
import {
  isLocalStorageEncryptionKeyReady,
  registerLocalStorageEncryptionWarningHandler,
  setLocalStorageEncryptionKey,
} from '../utility/local-storage-key-state.util';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageKeyService {
  private readonly telemetryLogger = inject(TelemetryLoggerService);
  private readonly encryptionLogger =
    this.telemetryLogger.forContext('LocalStorageEncryption');

  constructor() {
    registerLocalStorageEncryptionWarningHandler((message, context) => {
      this.encryptionLogger.warn(message, context);
    });
  }

  public initializeFromAccount(account: AccountInfo, clientId: string): void {
    const material = buildLocalStorageKeyMaterial(
      account.homeAccountId ?? '',
      account.localAccountId ?? '',
      clientId
    );
    setLocalStorageEncryptionKey(deriveLocalStorageEncryptionKey(material));
    migrateAllPlainLocalStorageKeys();
  }

  public initializeFromMsalInstance(
    msalInstance: IPublicClientApplication,
    clientId: string
  ): boolean {
    try {
      const account =
        msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

      if (!account) {
        return false;
      }

      this.initializeFromAccount(account, clientId);
      return true;
    } catch (error) {
      this.encryptionLogger.warn('MSAL encryption key init failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  public isEncryptionKeyReady(): boolean {
    return isLocalStorageEncryptionKeyReady();
  }

  public migratePlainKeys(): void {
    migrateAllPlainLocalStorageKeys();
  }

  public clear(): void {
    setLocalStorageEncryptionKey(null);
    clearEncryptedStorageReadCache();
  }
}
